import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { toOrder, COLUMNS, DELIVERED } from "@/lib/workshop";
import { useAuth } from "@/lib/AuthContext";

const Ctx = createContext(null);
const SELECT = "*, vehiculos!inner(*, clientes(*))";

const useSetting = (key, initial) => {
  const [val, setVal] = useState(() => localStorage.getItem(key) || initial);
  const save = (v) => { localStorage.setItem(key, v); setVal(v); };
  return [val, save];
};

export function OrdersProvider({ children }) {
  const { profile, profileError } = useAuth();
  const tallerId = profile?.taller_id;
  const [orders, setOrders] = useState([]);
  const [delivered, setDelivered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyUrgent, setOnlyUrgent] = useState(false);
  const [branch, setBranch] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [workshopName, setWorkshopName] = useSetting("taller_nombre", "Taller Pro");
  const [branchName, setBranchName] = useSetting("taller_sucursal", "Sucursal Principal");

  const load = useCallback(async () => {
    if (!tallerId) { setOrders([]); setDelivered([]); setLoading(false); return; }
    const base = () => supabase.from("ordenes_reparacion").select(SELECT).eq("vehiculos.taller_id", tallerId);
    const [active, done] = await Promise.all([
      base().or(`estado.is.null,estado.neq.${DELIVERED}`).order("created_at", { ascending: false }),
      base().eq("estado", DELIVERED).order("created_at", { ascending: false }),
    ]);
    setLoading(false);
    const error = active.error || done.error;
    if (error) { setConnected(false); console.error(error); return; }
    setConnected(true);
    setOrders((active.data || []).map(toOrder));
    setDelivered((done.data || []).map(toOrder));
  }, [tallerId]);

  const notify = (text) =>
    setNotifications((n) => [{ id: Date.now() + Math.random(), text, at: new Date() }, ...n].slice(0, 30));

  useEffect(() => {
    load();
    const ch = supabase
      .channel("ordenes_reparacion_rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "ordenes_reparacion" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [load]);

  const fail = (msg, error) => { console.error(error); toast.error(msg, { description: error?.message }); };

  const updateOrder = async (id, patch, dbPatch) => {
    const prev = orders;
    const prevDone = delivered;
    const apply = (os) => os.map((o) => (o.id === id ? { ...o, ...patch } : o));
    setOrders(apply);
    setDelivered(apply);
    const { error } = await supabase.from("ordenes_reparacion").update(dbPatch).eq("id", id);
    if (error) { setOrders(prev); setDelivered(prevDone); fail("No se pudo guardar el cambio", error); return false; }
    return true;
  };

  const moveOrder = async (id, estado) => {
    const o = orders.find((x) => x.id === id);
    if (!o || o.estado === estado) return;
    const ok = await updateOrder(id, { estado }, { estado });
    if (ok) {
      const label = COLUMNS.find((c) => c.id === estado)?.label;
      notify(`${o.matricula || "Orden"} → ${label}`);
    }
  };

  const togglePago = async (id) => {
    const o = [...orders, ...delivered].find((x) => x.id === id);
    const pagado = !o.pagado;
    const ok = await updateOrder(id, { pagado }, { estado_pago: pagado ? "pagado" : "pendiente" });
    if (ok) notify(`${o.matricula || "Orden"}: pago ${pagado ? "registrado" : "marcado pendiente"}`);
  };

  const markDelivered = async (id) => {
    const o = orders.find((x) => x.id === id);
    if (!o) return;
    const prev = orders;
    setOrders((os) => os.filter((x) => x.id !== id));
    const { error } = await supabase.from("ordenes_reparacion").update({ estado: DELIVERED }).eq("id", id);
    if (error) { setOrders(prev); fail("No se pudo marcar como entregado", error); return; }
    setDelivered((d) => [{ ...o, estado: DELIVERED }, ...d]);
    notify(`${o.matricula || "Orden"} entregado al cliente`);
    toast.success("Orden entregada", { description: "La encontrarás en Historial de Entregados." });
  };

  const toggleUrgente = async (id) => {
    const o = orders.find((x) => x.id === id);
    const urgente = !o.urgente;
    const ok = await updateOrder(id, { urgente }, { prioritaria: urgente });
    if (ok) notify(`${o.matricula || "Orden"}: ${urgente ? "marcada como urgente" : "ya no es urgente"}`);
  };

  const ensureVehiculo = async (f) => {
    const mat = f.matricula.trim().toUpperCase();
    const known = orders.find((o) => o.matricula.toUpperCase() === mat && o.vehiculoId);
    const tel = f.telefono.replace(/\D+/g, "");
    if (known) {
      const { error } = await supabase.from("vehiculos").update({ Telefono: tel }).eq("id", known.vehiculoId);
      if (error) throw error;
      return known.vehiculoId;
    }
    const sameClient = orders.find((o) => o.clienteId &&
      o.cliente.trim().toLowerCase() === f.cliente.trim().toLowerCase() &&
      (!tel || o.telefono.replace(/\D+/g, "") === tel));
    let clienteId = sameClient?.clienteId;
    if (!clienteId) {
      const { data: cli, error: e1 } = await supabase.from("clientes")
        .insert({ nombre: f.cliente.trim(), telefono: tel })
        .select().single();
      if (e1) throw e1;
      clienteId = cli.id;
    }
    const { data: veh, error: e2 } = await supabase.from("vehiculos")
      .insert({ cliente_id: clienteId, matricula: mat, marca_modelo: f.modelo.trim(), Telefono: tel, taller_id: tallerId })
      .select().single();
    if (e2) throw e2;
    return veh.id;
  };

  const createOrder = async (f) => {
    if (!tallerId) {
      toast.error("No se pudo registrar la orden", { description: profileError || "Cargando el taller de tu usuario…" });
      return false;
    }
    try {
      const vehiculo_id = await ensureVehiculo(f);
      const { error } = await supabase.from("ordenes_reparacion").insert({
        vehiculo_id, servicio: f.servicio.trim(), estado: "pendiente_revision",
        estado_pago: "pendiente", importe_estimado: Number(f.importe) || 0,
        ...(f.urgente ? { prioritaria: true } : {}),
      });
      if (error) throw error;
      notify(`Nueva orden: ${f.matricula.toUpperCase()} · ${f.cliente}`);
      toast.success("Orden registrada");
      await load();
      return true;
    } catch (error) { fail("No se pudo registrar la orden", error); return false; }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = orders.filter((o) => (!onlyUrgent || o.urgente) && (!q ||
      [o.cliente, o.modelo, o.matricula, o.servicio, o.telefono].join(" ").toLowerCase().includes(q)));
    return [...list].sort((a, b) => Number(b.urgente) - Number(a.urgente));
  }, [orders, search, onlyUrgent]);

  const allOrders = useMemo(() => [...orders, ...delivered], [orders, delivered]);
  const searchAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allOrders;
    return allOrders.filter((o) =>
      [o.cliente, o.modelo, o.matricula, o.servicio, o.telefono].join(" ").toLowerCase().includes(q));
  }, [allOrders, search]);

  const value = {
    orders, filtered, delivered, allOrders, searchAll, markDelivered, loading, connected, search, setSearch, branch, setBranch, onlyUrgent, setOnlyUrgent,
    notifications, setNotifications, moveOrder, togglePago, toggleUrgente, createOrder, reload: load,
    workshopName, setWorkshopName, branchName, setBranchName, tallerId,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useOrders = () => useContext(Ctx);
